import "./trustedSecurityPolicies";
import { Suspense, useEffect } from "react";
import App from "./App";
// react library for routing
import {
  Routes,
  Route,
  useLocation,
  useNavigationType,
  createRoutesFromChildren,
  BrowserRouter,
  Navigate,
  matchRoutes,
} from "react-router-dom";

import "@mantine/core/styles.css";
import "@mantine/notifications/styles.css";

// Uppy plugin style
import "@uppy/core/dist/style.css";
import "@uppy/dashboard/dist/style.css";
import "@uppy/screen-capture/dist/style.css";

// plugins styles from node_modules
import "sweetalert2/dist/sweetalert2.min.css";
// rsuite styles
import "rsuite/dist/rsuite.min.css"; // or 'rsuite/dist/rsuite.min.css'

// quill editor styles
import "react-quill-new/dist/quill.snow.css";
import "../node_modules/quill-mention/dist/quill.mention.min.css";

// core styles
import "assets/scss/argon-dashboard-pro-react.scss?v1.2.0";
import "@mdi/font/css/materialdesignicons.min.css";
import "../node_modules/react-grid-layout/css/styles.css";
import "../node_modules/react-resizable/css/styles.css";
import "country-flag-icons/1x1/flags.css";
import "./index.css";

import FormLayout from "./layouts/FormLayout";
import { lazy } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";

// Toast Refactor with Mantine components
import NotificationWrapper from "./components/Notification/NotificationWrapper";

// React 18 Support
import { createRoot } from "react-dom/client";
import CreateCloudView from "./views/cloud/CreateCloudView/CreateCloudView";
import Cookies from "js-cookie";

// Progressive Web App
// import { swDev } from "./swDev";
import AppHelmet from "./components/AppComponents/AppHelmet";
import * as Sentry from "@sentry/react";
import ShareRecordLayout from "./layouts/ShareRecordLayout";
import SharedViewLayout from "./layouts/SharedViewLayout/SharedViewLayout";
import SharedPageLayout from "./layouts/SharedPageLayout/SharedPageLayout";
import AccountLayout from "./layouts/AccountLayout";
import Constants from "./utils/Constants";
import LoadingView from "./views/other/LoadingView";
import CrashView from "./views/other/CrashView";
import UserContext from "./context/UserContext/UserContext";
import CRMLayout from "./layouts/CRMLayout";
import LoginPage from "./views/auth/Login";
import ForgotPassword from "./views/auth/ForgotPassword";
import ResetPassword from "./views/auth/ResetPassword";
import Register from "./views/auth/Register";
import RegisterExisting from "./views/auth/RegisterExisting";
import SendPhoneVerification from "./views/auth/SendPhoneVerification";
import EnterPhoneVerification from "./views/auth/EnterPhoneVerification";
import CloudListView from "./views/cloud/CloudListView";
import SharedRecordPageLayout from "./layouts/SharedRecordPageLayout/SharedRecordPageLayout";
import { ErrorBoundary } from "react-error-boundary";
import SharedDocumentLayout from "./layouts/SharedDocumentLayout/SharedDocumentLayout";
import OAuthLoader from "./views/auth/OAuthLoader";
// Uncomment this for Jotai DevTools
// import { DevTools } from "jotai-devtools";
// import "jotai-devtools/styles.css";

const PrivacyPolicy = lazy(() => import("./views/other/PrivacyPolicy"));
const TermsAndConditions = lazy(
  () => import("./views/other/TermsAndConditions")
);

let sentryDSN: string = "";

if (
  window.location.href.indexOf("localhost") === -1 &&
  window.location.href.indexOf("127.0.0.1") === -1
) {
  sentryDSN =
    "https://67e3053bf75246cd17381f8084dac811@o4506192520740864.ingest.sentry.io/4506224760258560";
}

Sentry.init({
  dsn: sentryDSN,
  integrations: [
    Sentry.reactRouterV6BrowserTracingIntegration({
      useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),
  ],
  // Performance Monitoring
  tracesSampleRate: 1.0, // Capture 100% of the transactions
  tracePropagationTargets: [
    /^https:\/\/app\.growthcloud\.com/,
    /^https:\/\/app-staging\.growthcloud\.com/,
  ],

  debug: true,
});

const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

if (window && window.location && window.location.href) {
  const w: any = window;
  if (
    window.location.href.indexOf("localhost") >= 0 ||
    window.location.href.indexOf("127.0.0.1") >= 0
  ) {
    w.GC_ENVIRONMENT = Constants.ENVIRONMENT.DEVELOPMENT;
    w.GC_API_URL = Constants.API_URL.DEVELOPMENT;
  } else if (window.location.href.indexOf("app-staging") >= 0) {
    w.GC_ENVIRONMENT = Constants.ENVIRONMENT.STAGING;
    w.GC_API_URL = Constants.API_URL.STAGING;
  } else {
    w.GC_ENVIRONMENT = Constants.ENVIRONMENT.PRODUCTION;
    w.GC_API_URL = Constants.API_URL.PRODUCTION;
  }
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // refetchOnWindowFocus: false,
      retry: 3,
      retryDelay: 2000,
      staleTime: 30000,
      notifyOnChangeProps: ["data", "error", "isLoading"],
    },
  },
});

// React 18 Support
const container = document.getElementById("root")!;
const root = createRoot(container);

// Progressive Web App
//swDev();

root.render(
  <ErrorBoundary FallbackComponent={CrashView}>
    <Suspense fallback={<LoadingView />}>
      <QueryClientProvider client={queryClient}>
        <App>
          <BrowserRouter>
            <NotificationWrapper>
              <AppHelmet title="GrowthCloud.com - Revenue growth made easy." />
              <UserContext
                disabled={window.location.pathname.startsWith("/forms")}
              >
                <SentryRoutes>
                  <Route
                    path="/shared-view/:tenantID/:sharedViewID/:recordID"
                    element={<SharedViewLayout />}
                  />
                  <Route
                    path="/shared-view/:tenantID/:sharedViewID"
                    element={<SharedViewLayout />}
                  />
                  <Route
                    path="/shared-page/:tenantID/:sharedPageID"
                    element={<SharedPageLayout />}
                  />
                  <Route
                    path="/shared-page/:tenantID/:sharedPageID/:viewID"
                    element={<SharedPageLayout />}
                  />
                  <Route
                    path="/shared-document/:tenantID/:sharedDocID"
                    element={<SharedDocumentLayout />}
                  />
                  <Route
                    path="/shared-record-page/:tenantID/:sharedRecordPageID/:viewID"
                    element={<SharedRecordPageLayout />}
                  />
                  <Route
                    path="/share/:sharedRecordID"
                    element={<ShareRecordLayout />}
                  />
                  <Route
                    path="/forms/:tenantID/:formID"
                    element={<FormLayout />}
                  />
                  <Route
                    path="/account/:tab/*"
                    element={<AccountLayout backArrow={true} />}
                  />
                  <Route path="/clouds/:tenantID/*" element={<CRMLayout />} />
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/oauth" element={<OAuthLoader />} />
                  <Route path="/login/forgot" element={<ForgotPassword />} />
                  <Route path="/login/reset" element={<ResetPassword />} />
                  <Route path="/register" element={<Register />} />
                  <Route
                    path="/register/existing"
                    element={<RegisterExisting />}
                  />
                  <Route
                    path="/register/phone"
                    element={<SendPhoneVerification />}
                  />
                  <Route
                    path="/register/phone/verify"
                    element={<EnterPhoneVerification />}
                  />
                  <Route path="/clouds" element={<CloudListView />} />
                  <Route path="/create/cloud" element={<CreateCloudView />} />
                  <Route path="/privacy" element={<PrivacyPolicy />} />
                  <Route path="/terms" element={<TermsAndConditions />} />
                  <Route
                    path="*"
                    element={
                      <Navigate
                        to={
                          Cookies.get("authenticationToken")
                            ? "/clouds"
                            : "/login"
                        }
                        replace
                      />
                    }
                  />
                </SentryRoutes>
              </UserContext>
            </NotificationWrapper>
          </BrowserRouter>
          <ReactQueryDevtools buttonPosition="bottom-left" />
        </App>
      </QueryClientProvider>
      {/* Uncomment this for Jotai DevTools */}
      {/* <DevTools position="top-left" /> */}
    </Suspense>
  </ErrorBoundary>
);
